import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import AuditTrail from "../components/AuditTrail";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const WORKFLOW_LABELS = {
  INDENTER: "Indenter", HOD: "HoD", PI: "PI", COMMITTEE: "Committee",
  SNP_DA: "S&P DA", SNP_AR: "S&P AR", SNP_HOS: "S&P HoS",
  INTERNAL_AUDITOR: "Int. Auditor", FNA_DA: "F&A DA", FNA_AR: "F&A AR", FNA_HOS: "F&A HoS",
  DEAN: "Dean (Admin)", DEAN_SRC: "Dean (SRC)", DEAN_NONGEM: "Dean (Non-GeM)",
  DEAN_SRC_NONGEM: "Dean SRC (Non-GeM)", DIRECTOR: "Director", SNP_DA_FINAL: "S&P (PO)"
};

const STATUS_CONFIG = {
  PENDING: { label: "Pending", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  APPROVED: { label: "Approved", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  REJECTED: { label: "Rejected", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  RETURNED: { label: "Returned", cls: "bg-blue-100 text-blue-800 border-blue-300" },
};

const DOC_TYPES = [
  { value: "PAC_CERTIFICATE", label: "PAC Certificate" },
  { value: "TECHNICAL_SPECS", label: "Technical Specifications" },
  { value: "JUSTIFICATION_NOTE", label: "Justification Note (Non-GeM)" },
  { value: "COMPARATIVE_STATEMENT", label: "Comparative Statement" },
  { value: "PROPRIETARY_JUSTIFICATION", label: "Proprietary / Single Tender Justification" },
  { value: "QUOTATION", label: "Quotation / Price Bid" },
  { value: "EMD_EXEMPTION_PROOF", label: "EMD Exemption Proof" },
  { value: "PBG_DOCUMENT", label: "PBG / Performance Security Document" },
  { value: "INTEGRITY_PACT", label: "Signed Integrity Pact" },
  { value: "DC_TECH_SPEC", label: "Datacentre - Technical Specification" },
  { value: "DC_RACK_LAYOUT", label: "Datacentre - Rack Layout Diagram" },
  { value: "DC_POWER_SHEET", label: "Datacentre - Power Consumption Sheet" },
  { value: "OTHER", label: "Other Supporting Document" },
];

const el = React.createElement;

function InfoField({ label, value }) {
  return el("div", null,
    el("p", { className: "text-xs text-slate-500 uppercase tracking-wider font-medium" }, label),
    el("p", { className: "text-sm font-medium text-slate-900 mt-0.5" }, value || "-")
  );
}

function WorkflowStep({ step, isCompleted, isCurrent, isRejected, isReturned, historyEntry, isLast }) {
  return el("div", { className: "flex gap-3", "data-testid": "workflow-step-" + step },
    el("div", { className: "flex flex-col items-center" },
      el("div", { className: "w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 " +
        (isCompleted ? "bg-emerald-500 border-emerald-500" :
         isRejected ? "bg-rose-500 border-rose-500" :
         isReturned ? "bg-blue-500 border-blue-500" :
         isCurrent ? "bg-amber-400 border-amber-400 animate-pulse" :
         "bg-white border-slate-300")
      }),
      !isLast && el("div", { className: "w-0.5 flex-1 min-h-[2rem] " + (isCompleted ? "bg-emerald-300" : "bg-slate-200") })
    ),
    el("div", { className: "pb-4 flex-1 min-w-0" },
      el("p", { className: "text-sm font-semibold " +
        (isCompleted ? "text-emerald-700" :
         isCurrent ? "text-amber-700" :
         isRejected ? "text-rose-700" :
         isReturned ? "text-blue-700" :
         "text-slate-400")
      }, WORKFLOW_LABELS[step] || step),
      historyEntry && el("div", { className: "mt-1" },
        el("p", { className: "text-xs text-slate-500" },
          historyEntry.user_name + " - " + new Date(historyEntry.timestamp).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
        ),
        historyEntry.remarks && el("p", { className: "text-xs text-slate-600 italic mt-0.5" }, '"' + historyEntry.remarks + '"')
      ),
      isCurrent && !historyEntry && el("p", { className: "text-xs text-amber-600 mt-1" }, "Awaiting action...")
    )
  );
}

function DocItem({ doc, canDelete, onDownload, onDelete }) {
  return el("div", { className: "flex items-center gap-3 p-3 bg-white border rounded-lg hover:bg-slate-50 transition-colors", "data-testid": "doc-" + doc.id },
    el("div", { className: "w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0" },
      el("span", { className: "text-blue-600 text-xs font-bold" }, (doc.original_filename || "").split(".").pop()?.toUpperCase() || "F")
    ),
    el("div", { className: "flex-1 min-w-0" },
      el("p", { className: "text-sm font-medium truncate" }, doc.original_filename),
      el("p", { className: "text-xs text-slate-500" },
        (DOC_TYPES.find(d => d.value === doc.document_type)?.label || doc.document_type) + " · " + doc.uploaded_by_name
      )
    ),
    el("div", { className: "flex gap-1" },
      el(Button, { variant: "ghost", size: "sm", onClick: () => onDownload(doc), className: "text-xs" }, "Download"),
      canDelete && el(Button, { variant: "ghost", size: "sm", onClick: () => onDelete(doc.id), className: "text-xs text-rose-500" }, "Delete")
    )
  );
}

function HistoryEntry({ entry }) {
  const actionCls = entry.action === "APPROVE" ? "bg-emerald-50 border-emerald-200" :
                    entry.action === "REJECT" ? "bg-rose-50 border-rose-200" :
                    "bg-blue-50 border-blue-200";
  const badgeCls = entry.action === "APPROVE" ? "bg-emerald-100 text-emerald-800" :
                   entry.action === "REJECT" ? "bg-rose-100 text-rose-800" :
                   "bg-blue-100 text-blue-800";
  return el("div", { className: "p-3 rounded-lg border text-sm " + actionCls },
    el("div", { className: "flex items-center justify-between" },
      el("span", { className: "font-semibold" }, entry.user_name),
      el("span", { className: "text-xs px-2 py-0.5 rounded-full font-medium " + badgeCls }, entry.action)
    ),
    el("p", { className: "text-xs text-slate-500 mt-1" },
      (WORKFLOW_LABELS[entry.level] || entry.level) + " - " + new Date(entry.timestamp).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    ),
    entry.remarks && el("p", { className: "text-xs text-slate-700 mt-1 italic" }, '"' + entry.remarks + '"')
  );
}

function ItemRow({ item, idx }) {
  return el("tr", { className: "border-b last:border-0 hover:bg-slate-50/50" },
    el("td", { className: "py-3 px-5 text-slate-500" }, item.serial_no),
    el("td", { className: "py-3 px-5 font-medium text-slate-900 max-w-xs" }, item.description),
    el("td", { className: "py-3 px-5 text-slate-500" }, item.hsn_code || "-"),
    el("td", { className: "py-3 px-5 text-right" }, item.quantity),
    el("td", { className: "py-3 px-5 text-slate-500" }, item.unit),
    el("td", { className: "py-3 px-5 text-right" }, "\u20B9" + Number(item.estimated_cost || 0).toLocaleString("en-IN")),
    el("td", { className: "py-3 px-5 text-right font-semibold" }, "\u20B9" + (Number(item.estimated_cost || 0) * Number(item.quantity || 1)).toLocaleString("en-IN"))
  );
}

function FundingSourceRow({ src, idx }) {
  return el("div", { className: "flex items-center gap-4 p-3 bg-slate-50 rounded-lg text-sm" },
    el("span", { className: "font-medium w-20" }, src.fund_type),
    el("span", { className: "text-slate-500" }, src.budget_head || "-"),
    el("span", { className: "ml-auto font-semibold" }, "\u20B9" + Number(src.amount || 0).toLocaleString("en-IN"))
  );
}

function RequisitionView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, token, getAuthHeader } = useAuth();
  const fileInputRef = useRef(null);

  const [requisition, setRequisition] = useState(null);
  const [auditTrail, setAuditTrail] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState("TECHNICAL_SPECS");
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (id) fetchAll(); }, [id]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [reqRes, auditRes, docsRes] = await Promise.all([
        axios.get(API_URL + "/requisitions/" + id, getAuthHeader()),
        axios.get(API_URL + "/requisitions/" + id + "/audit-trail", getAuthHeader()),
        axios.get(API_URL + "/documents/by-requisition/" + id, getAuthHeader()),
      ]);
      setRequisition(reqRes.data);
      setAuditTrail(auditRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      toast.error("Failed to load requisition");
      navigate("/requisitions");
    } finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(
        API_URL + "/documents/upload?requisition_id=" + id + "&document_type=" + selectedDocType,
        fd, { headers: { Authorization: "Bearer " + token, "Content-Type": "multipart/form-data" } }
      );
      toast.success("Document uploaded successfully");
      const docsRes = await axios.get(API_URL + "/documents/by-requisition/" + id, getAuthHeader());
      setDocuments(docsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(API_URL + "/documents/" + doc.id + "/download", {
        ...getAuthHeader(), responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.original_filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(API_URL + "/documents/" + docId, getAuthHeader());
      toast.success("Document removed");
      setDocuments(prev => prev.filter(d => d.id !== docId));
    } catch { toast.error("Failed to delete document"); }
  };

  const handleApproval = async (action) => {
    setSubmitting(true);
    try {
      await axios.put(API_URL + "/requisitions/" + id + "/approve", { action: action, remarks: approvalRemarks }, getAuthHeader());
      toast.success("Requisition " + action.toLowerCase() + "d successfully");
      setApprovalRemarks("");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return el("div", { className: "flex items-center justify-center min-h-96" },
      el("div", { className: "text-center" },
        el("div", { className: "w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" }),
        el("p", { className: "text-slate-500" }, "Loading requisition...")
      )
    );
  }
  if (!requisition) return null;

  const r = requisition;
  const statusConf = STATUS_CONFIG[r.status] || STATUS_CONFIG.PENDING;
  const isNonDirect = r.procurement_mode !== "DIRECT_PURCHASE";
  const workflow = auditTrail?.approval_workflow || r.approval_workflow || [];
  const history = auditTrail?.approval_history || [];
  const currentLevel = auditTrail?.current_approval_level || r.current_approval_level;
  const completedSteps = new Set(history.filter(h => h.action === "APPROVE").map(h => h.level));

  return el("div", { className: "max-w-6xl space-y-6", "data-testid": "requisition-view-page" },

    // Header
    el("div", { className: "flex items-center justify-between" },
      el("div", { className: "flex items-center gap-4" },
        el(Button, { variant: "outline", onClick: () => navigate("/requisitions"), "data-testid": "back-btn" }, "\u2190 Back"),
        el("div", null,
          el("h1", { className: "text-2xl font-bold text-slate-900 tracking-tight" }, r.requisition_number),
          el("p", { className: "text-slate-500 text-sm mt-0.5" },
            "Created " + new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
          )
        )
      ),
      el("div", { className: "flex items-center gap-3" },
        el(Button, { variant: "outline", size: "sm", onClick: () => navigate("/purchase-orders/new?from_pr=" + r.id), "data-testid": "create-po-from-pr" }, "Create PO"),
        el(Button, { variant: "outline", size: "sm", onClick: () => navigate("/grn/new?from_po_pr=" + r.id), "data-testid": "create-grn-from-pr" }, "Create GRN"),
        el("div", { className: "flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold " + statusConf.cls, "data-testid": "status-badge" },
          statusConf.label
        )
      )
    ),

    // Approval Actions - only show to the authorized approver
    r.status === "PENDING" && (() => {
      const level = r.current_approval_level;
      const role = user?.role;
      const roleMap = {
        HOD: ["HOD"], PI: ["PI"],
        SNP_DA: ["SNP_DA"], SNP_AR: ["SNP_AR"], SNP_HOS: ["SNP_HOS"],
        FNA_DA: ["FNA_DA"], FNA_AR: ["FNA_AR"], FNA_HOS: ["FNA_HOS"],
        INTERNAL_AUDITOR: ["INTERNAL_AUDITOR"], DEAN: ["DEAN"], DEAN_SRC: ["DEAN_SRC"],
        DEAN_NONGEM: ["DEAN"], DEAN_SRC_NONGEM: ["DEAN_SRC"],
        DIRECTOR: ["DIRECTOR"], SNP_DA_FINAL: ["SNP_DA"]
      };
      return (roleMap[level] || []).includes(role);
    })() && el(Card, { className: "border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50", "data-testid": "approval-actions" },
      el(CardContent, { className: "p-5" },
        el("p", { className: "text-sm font-semibold text-amber-900 mb-3" }, "Approval Action Required"),
        el(Textarea, { value: approvalRemarks, onChange: (e) => setApprovalRemarks(e.target.value), placeholder: "Add remarks (optional)...", className: "mb-3 bg-white", rows: 2 }),
        el("div", { className: "flex gap-3" },
          el(Button, { onClick: () => handleApproval("APPROVE"), className: "bg-emerald-600 hover:bg-emerald-700", disabled: submitting, "data-testid": "approve-btn" }, "Approve"),
          el(Button, { variant: "outline", onClick: () => handleApproval("RETURN"), disabled: submitting, "data-testid": "return-btn" }, "Return"),
          el(Button, { variant: "outline", onClick: () => handleApproval("REJECT"), className: "text-rose-600 border-rose-200 hover:bg-rose-50", disabled: submitting, "data-testid": "reject-btn" }, "Reject")
        )
      )
    ),

    // Pending indicator for non-approvers
    r.status === "PENDING" && !(() => {
      const level = r.current_approval_level;
      const role = user?.role;
      const roleMap = {
        HOD: ["HOD"], PI: ["PI"],
        SNP_DA: ["SNP_DA"], SNP_AR: ["SNP_AR"], SNP_HOS: ["SNP_HOS"],
        FNA_DA: ["FNA_DA"], FNA_AR: ["FNA_AR"], FNA_HOS: ["FNA_HOS"],
        INTERNAL_AUDITOR: ["INTERNAL_AUDITOR"], DEAN: ["DEAN"], DEAN_SRC: ["DEAN_SRC"],
        DEAN_NONGEM: ["DEAN"], DEAN_SRC_NONGEM: ["DEAN_SRC"],
        DIRECTOR: ["DIRECTOR"], SNP_DA_FINAL: ["SNP_DA"]
      };
      return (roleMap[level] || []).includes(role);
    })() && el("div", { className: "px-4 py-3 rounded-lg bg-slate-100 border border-slate-200 text-sm text-slate-600", "data-testid": "pending-indicator" },
      "Pending with: ", el("span", { className: "font-semibold" }, WORKFLOW_LABELS[r.current_approval_level] || r.current_approval_level)
    ),

    // Summary Strip
    el("div", { className: "grid grid-cols-2 md:grid-cols-5 gap-4" },
      el(Card, { className: "p-4" },
        el("p", { className: "text-xs text-slate-500 uppercase tracking-wider font-medium" }, "Channel"),
        el("p", { className: "text-lg font-bold mt-1 " + (r.procurement_channel === "GEM" ? "text-green-700" : "text-orange-700") },
          r.procurement_channel === "GEM" ? "GeM" : "Non-GeM"
        )
      ),
      el(Card, { className: "p-4" },
        el("p", { className: "text-xs text-slate-500 uppercase tracking-wider font-medium" }, "Classification"),
        el("p", { className: "text-lg font-bold mt-1 text-slate-900" }, r.item_classification === "SCIENTIFIC" ? "Scientific" : "General")
      ),
      el(Card, { className: "p-4" },
        el("p", { className: "text-xs text-slate-500 uppercase tracking-wider font-medium" }, "Estimated Value"),
        el("p", { className: "text-lg font-bold mt-1 text-slate-900" }, "\u20B9" + (r.estimated_value || 0).toLocaleString("en-IN"))
      ),
      el(Card, { className: "p-4" },
        el("p", { className: "text-xs text-slate-500 uppercase tracking-wider font-medium" }, "Mode"),
        el("p", { className: "text-sm font-bold mt-1 text-indigo-700" }, r.procurement_label || (r.procurement_mode || "").replace(/_/g, " "))
      ),
      el(Card, { className: "p-4" },
        el("p", { className: "text-xs text-slate-500 uppercase tracking-wider font-medium" }, "Committee"),
        el("p", { className: "text-lg font-bold mt-1 text-slate-900" }, r.committee_required > 0 ? r.committee_required + " Members" : "Not Required")
      )
    ),

    el("div", { className: "grid grid-cols-1 lg:grid-cols-3 gap-6" },

      // Left Column: Details
      el("div", { className: "lg:col-span-2 space-y-6" },

        // Indenter Info
        el(Card, null,
          el(CardHeader, { className: "border-b bg-slate-50 py-3 px-5" }, el(CardTitle, { className: "text-base" }, "Indenter Information")),
          el(CardContent, { className: "p-5 grid grid-cols-2 md:grid-cols-4 gap-4" },
            el(InfoField, { label: "Name", value: r.indenter_name }),
            el(InfoField, { label: "Department", value: r.department }),
            el(InfoField, { label: "Purpose", value: r.purpose === "SCIENTIFIC" ? "Scientific / Research" : "General" }),
            el(InfoField, { label: "Category", value: (r.category || "").replace(/_/g, " ") })
          )
        ),

        // Funding
        el(Card, null,
          el(CardHeader, { className: "border-b bg-emerald-50 py-3 px-5" }, el(CardTitle, { className: "text-base text-emerald-900" }, "Funding Information")),
          el(CardContent, { className: "p-5" },
            el("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-4" },
              el(InfoField, { label: "Funding Mode", value: r.funding_mode === "MULTI" ? "Multi-Funding" : "Single Source" }),
              el(InfoField, { label: "Fund Type", value: r.fund_type }),
              el(InfoField, { label: "Budget Head", value: r.budget_head || "-" }),
              el(InfoField, { label: "Project", value: r.project_name || r.project_number || "-" })
            ),
            r.funding_mode === "MULTI" && (r.funding_sources || []).length > 0 && el("div", { className: "mt-4 border-t pt-4" },
              el("p", { className: "text-sm font-semibold text-slate-700 mb-2" }, "Funding Sources"),
              el("div", { className: "space-y-2" },
                ...(r.funding_sources || []).map((src, i) => el(FundingSourceRow, { key: i, src: src, idx: i }))
              )
            )
          )
        ),

        // Items Table
        el(Card, null,
          el(CardHeader, { className: "border-b bg-slate-50 py-3 px-5" }, el(CardTitle, { className: "text-base" }, "Items (" + (r.items || []).length + ")")),
          el(CardContent, { className: "p-0" },
            el("table", { className: "w-full text-sm" },
              el("thead", null,
                el("tr", { className: "border-b bg-slate-50/50" },
                  el("th", { className: "text-left py-3 px-5 font-semibold text-slate-600" }, "#"),
                  el("th", { className: "text-left py-3 px-5 font-semibold text-slate-600" }, "Description"),
                  el("th", { className: "text-left py-3 px-5 font-semibold text-slate-600" }, "HSN"),
                  el("th", { className: "text-right py-3 px-5 font-semibold text-slate-600" }, "Qty"),
                  el("th", { className: "text-left py-3 px-5 font-semibold text-slate-600" }, "Unit"),
                  el("th", { className: "text-right py-3 px-5 font-semibold text-slate-600" }, "Unit Cost"),
                  el("th", { className: "text-right py-3 px-5 font-semibold text-slate-600" }, "Total")
                )
              ),
              el("tbody", null,
                ...(r.items || []).map((item, i) => el(ItemRow, { key: i, item: item, idx: i }))
              ),
              el("tfoot", null,
                el("tr", { className: "bg-slate-900 text-white" },
                  el("td", { colSpan: 6, className: "py-3 px-5 font-medium" }, "Total Estimated Value"),
                  el("td", { className: "py-3 px-5 text-right font-bold text-lg" }, "\u20B9" + (r.estimated_value || 0).toLocaleString("en-IN"))
                )
              )
            )
          )
        ),

        // Equipment & Delivery (only for non-direct)
        isNonDirect && el(Card, null,
          el(CardHeader, { className: "border-b bg-cyan-50 py-3 px-5" }, el(CardTitle, { className: "text-base text-cyan-900" }, "Equipment & Delivery Details")),
          el(CardContent, { className: "p-5 grid grid-cols-2 md:grid-cols-3 gap-4" },
            el(InfoField, { label: "Equipment Life", value: r.equipment_life }),
            el(InfoField, { label: "Warranty", value: r.warranty_details }),
            el(InfoField, { label: "AMC Required", value: r.amc_required }),
            el(InfoField, { label: "Delivery Period", value: r.delivery_months ? r.delivery_months + " months" : "-" }),
            el(InfoField, { label: "Installation", value: r.installation_ready ? "Required" : "Not Required" }),
            el(InfoField, { label: "Inspection", value: r.inspection_required ? "Required" : "Not Required" }),
            r.installation_remarks && el("div", { className: "col-span-3" }, el(InfoField, { label: "Installation Remarks", value: r.installation_remarks })),
            r.inspection_details && el("div", { className: "col-span-3" }, el(InfoField, { label: "Inspection Details", value: r.inspection_details }))
          )
        ),

        // Tender Details (only for non-direct with tender)
        isNonDirect && r.tender_mode && el(Card, null,
          el(CardHeader, { className: "border-b bg-violet-50 py-3 px-5" }, el(CardTitle, { className: "text-base text-violet-900" }, "Tender / Procurement Details")),
          el(CardContent, { className: "p-5 grid grid-cols-2 md:grid-cols-3 gap-4" },
            el(InfoField, { label: "Tender Mode", value: (r.tender_mode || "").replace(/_/g, " ") }),
            el(InfoField, { label: "Platform", value: r.tender_platform }),
            el(InfoField, { label: "Committee Date", value: r.purchase_committee_date ? new Date(r.purchase_committee_date).toLocaleDateString("en-IN") : "-" }),
            r.limited_tender_suppliers && el("div", { className: "col-span-3" }, el(InfoField, { label: "Limited Tender Suppliers", value: r.limited_tender_suppliers })),
            r.single_tender_justification && el("div", { className: "col-span-3" }, el(InfoField, { label: "Single Tender Justification", value: r.single_tender_justification }))
          )
        ),

        // Specs & Justifications
        (r.technical_specifications || r.pac_justification || r.justification_note) && el(Card, null,
          el(CardHeader, { className: "border-b bg-slate-50 py-3 px-5" }, el(CardTitle, { className: "text-base" }, "Specifications & Justifications")),
          el(CardContent, { className: "p-5 space-y-4" },
            r.technical_specifications && el("div", null,
              el("p", { className: "text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" }, "Technical Specifications"),
              el("p", { className: "text-sm text-slate-800 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg border" }, r.technical_specifications)
            ),
            r.pac_justification && el("div", null,
              el("p", { className: "text-xs font-semibold text-orange-600 uppercase tracking-wider mb-1" }, "PAC Justification"),
              el("p", { className: "text-sm text-slate-800 whitespace-pre-wrap bg-orange-50 p-3 rounded-lg border border-orange-200" }, r.pac_justification)
            ),
            r.justification_note && el("div", null,
              el("p", { className: "text-xs font-semibold text-amber-600 uppercase tracking-wider mb-1" }, "Non-GeM Justification"),
              el("p", { className: "text-sm text-slate-800 whitespace-pre-wrap bg-amber-50 p-3 rounded-lg border border-amber-200" }, r.justification_note)
            )
          )
        ),

        // Committee Members
        ((r.technical_committee_members || []).length > 0 || (r.purchase_committee_members || []).length > 0) && el(Card, null,
          el(CardHeader, { className: "border-b bg-amber-50 py-3 px-5" }, el(CardTitle, { className: "text-base text-amber-900" }, "Committee Members")),
          el(CardContent, { className: "p-5" },
            (r.technical_committee_members || []).length > 0 && el("div", null,
              el("p", { className: "text-sm font-semibold text-slate-700 mb-2" }, "Technical Committee"),
              el("div", { className: "space-y-2" },
                ...(r.technical_committee_members || []).map((m, i) =>
                  el("div", { key: i, className: "flex items-center gap-4 p-3 bg-slate-50 rounded-lg text-sm" },
                    el("span", { className: "px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full font-medium" }, m.role || "Member"),
                    el("span", { className: "font-medium" }, m.name),
                    el("span", { className: "text-slate-500" }, m.designation)
                  )
                )
              )
            )
          )
        ),

        // EMD Details (view)
        r.emd_applicable != null && el(Card, null,
          el(CardHeader, { className: "border-b bg-rose-50 py-3 px-5" }, el(CardTitle, { className: "text-base text-rose-900" }, "EMD Requirement Declaration")),
          el(CardContent, { className: "p-5 grid grid-cols-2 md:grid-cols-3 gap-4" },
            el(InfoField, { label: "EMD Applicable", value: r.emd_applicable ? "Yes" : "No" }),
            r.emd_applicable && el(InfoField, { label: "EMD Amount", value: r.emd_amount ? "\u20B9" + Number(r.emd_amount).toLocaleString("en-IN") : "-" }),
            r.emd_applicable && el(InfoField, { label: "EMD Form/Type", value: r.emd_form_type }),
            r.emd_applicable && el(InfoField, { label: "EMD Due Date", value: r.emd_due_date ? new Date(r.emd_due_date).toLocaleDateString("en-IN") : "-" }),
            el(InfoField, { label: "Exemption Applicable", value: r.emd_exemption_applicable ? "Yes" : "No" }),
            r.emd_exemption_applicable && el(InfoField, { label: "Exemption Criteria", value: r.emd_exemption_criteria })
          )
        ),

        // APS Details (view)
        r.aps_applicable && el(Card, null,
          el(CardHeader, { className: "border-b bg-teal-50 py-3 px-5" }, el(CardTitle, { className: "text-base text-teal-900" }, "APS / Performance Security")),
          el(CardContent, { className: "p-5 grid grid-cols-2 md:grid-cols-3 gap-4" },
            el(InfoField, { label: "Instrument Type", value: r.aps_instrument_type }),
            el(InfoField, { label: "PBG Reference", value: r.aps_pbg_reference }),
            el(InfoField, { label: "PBG Amount", value: r.aps_amount ? "\u20B9" + Number(r.aps_amount).toLocaleString("en-IN") : "-" }),
            el(InfoField, { label: "Date of Issue", value: r.aps_issue_date ? new Date(r.aps_issue_date).toLocaleDateString("en-IN") : "-" }),
            el(InfoField, { label: "Expiry Date", value: r.aps_expiry_date ? new Date(r.aps_expiry_date).toLocaleDateString("en-IN") : "-" }),
            el(InfoField, { label: "Bank Name", value: r.aps_bank_name }),
            el(InfoField, { label: "Vendor", value: r.aps_vendor_name })
          )
        ),

        // Integrity Pact (view)
        r.integrity_pact_applicable && el(Card, null,
          el(CardHeader, { className: "border-b bg-indigo-50 py-3 px-5" }, el(CardTitle, { className: "text-base text-indigo-900" }, "Integrity Pact")),
          el(CardContent, { className: "p-5 grid grid-cols-2 md:grid-cols-3 gap-4" },
            el(InfoField, { label: "Reference Number", value: r.integrity_pact_reference }),
            el(InfoField, { label: "Signing Date", value: r.integrity_pact_signing_date ? new Date(r.integrity_pact_signing_date).toLocaleDateString("en-IN") : "-" }),
            el(InfoField, { label: "Expiry Date", value: r.integrity_pact_expiry_date ? new Date(r.integrity_pact_expiry_date).toLocaleDateString("en-IN") : "-" }),
            el(InfoField, { label: "Vendor", value: r.integrity_pact_vendor_name }),
            r.integrity_pact_remarks && el("div", { className: "col-span-3" }, el(InfoField, { label: "Remarks", value: r.integrity_pact_remarks }))
          )
        ),

        // Datacentre (view)
        (r.datacentre_equipment || []).length > 0 && el(Card, null,
          el(CardHeader, { className: "border-b bg-sky-50 py-3 px-5" }, el(CardTitle, { className: "text-base text-sky-900" }, "Datacentre Requirements")),
          el(CardContent, { className: "p-5 space-y-3" },
            el("div", { className: "grid grid-cols-2 gap-4 mb-3" },
              el(InfoField, { label: "Fund Available", value: r.datacentre_fund_available ? "Yes" : "No" }),
              el(InfoField, { label: "Funding Source", value: r.datacentre_funding_source })
            ),
            ...(r.datacentre_equipment || []).map((eq, i) =>
              el("div", { key: i, className: "p-3 bg-slate-50 rounded-lg border grid grid-cols-4 gap-3 text-sm" },
                el("div", { className: "col-span-4" }, el(InfoField, { label: "Specifications", value: eq.specs })),
                el(InfoField, { label: "Qty", value: String(eq.quantity) }),
                el(InfoField, { label: "Rack Unit (RU)", value: String(eq.rack_unit) }),
                el(InfoField, { label: "Equipment Type", value: (eq.equipment_type || "").replace(/_/g, " ") }),
                el(InfoField, { label: "Power Required", value: (eq.power_required || "").replace(/_/g, " ") })
              )
            )
          )
        )
      ),

      // Right Column
      el("div", { className: "space-y-6" },

        // Approval Workflow
        el(Card, { "data-testid": "approval-progress" },
          el(CardHeader, { className: "border-b bg-gradient-to-r from-indigo-50 to-blue-50 py-3 px-5" },
            el(CardTitle, { className: "text-base text-indigo-900" }, "Approval Progress")
          ),
          el(CardContent, { className: "p-5" },
            el("div", { className: "space-y-0" },
              ...workflow.map((step, i) =>
                el(WorkflowStep, {
                  key: i,
                  step: step,
                  isCompleted: completedSteps.has(step),
                  isCurrent: step === currentLevel && r.status === "PENDING",
                  isRejected: r.status === "REJECTED" && step === currentLevel,
                  isReturned: r.status === "RETURNED" && step === currentLevel,
                  historyEntry: history.find(h => h.level === step),
                  isLast: i === workflow.length - 1
                })
              )
            )
          )
        ),

        // Audit Trail
        el(Card, { "data-testid": "audit-trail" },
          el(CardHeader, { className: "border-b bg-slate-50 py-3 px-5" }, el(CardTitle, { className: "text-base" }, "Activity Log")),
          el(CardContent, { className: "p-5" },
            history.length === 0
              ? el("p", { className: "text-sm text-slate-400 italic" }, "No activity recorded yet.")
              : el("div", { className: "space-y-3" },
                  ...[...history].reverse().map((entry, i) => el(HistoryEntry, { key: i, entry: entry }))
                )
          )
        ),

        // Documents
        el(Card, { "data-testid": "documents-section" },
          el(CardHeader, { className: "border-b bg-slate-50 py-3 px-5" }, el(CardTitle, { className: "text-base" }, "Documents (" + documents.length + ")")),
          el(CardContent, { className: "p-5 space-y-4" },
            // Upload Area
            el("div", { className: "p-4 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50/50 text-center" },
              el("p", { className: "text-sm text-slate-600 mb-3" }, "Upload supporting documents"),
              el("div", { className: "flex items-center gap-2 justify-center flex-wrap" },
                el("select", {
                  value: selectedDocType,
                  onChange: (e) => setSelectedDocType(e.target.value),
                  className: "text-sm p-2 border rounded-md",
                  "data-testid": "doc-type-select"
                },
                  ...DOC_TYPES.map(dt => el("option", { key: dt.value, value: dt.value }, dt.label))
                ),
                el(Button, {
                  type: "button", variant: "outline", size: "sm",
                  onClick: () => fileInputRef.current?.click(),
                  disabled: uploading,
                  "data-testid": "upload-btn"
                }, uploading ? "Uploading..." : "Choose File"),
                el("input", {
                  ref: fileInputRef, type: "file", className: "hidden",
                  accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.csv",
                  onChange: handleFileUpload
                })
              )
            ),
            // Document List
            documents.length === 0
              ? el("p", { className: "text-sm text-slate-400 italic text-center" }, "No documents uploaded yet.")
              : el("div", { className: "space-y-2" },
                  ...documents.map(doc => el(DocItem, {
                    key: doc.id, doc: doc,
                    canDelete: doc.uploaded_by === user?.id,
                    onDownload: handleDownload,
                    onDelete: handleDeleteDoc
                  }))
                )
          )
        ),

        // Certifications
        (r.items_not_in_store || r.specs_are_broad) && el(Card, null,
          el(CardHeader, { className: "border-b bg-slate-50 py-3 px-5" }, el(CardTitle, { className: "text-base" }, "Certifications")),
          el(CardContent, { className: "p-5 space-y-2" },
            r.items_not_in_store && el("div", { className: "flex items-center gap-2 text-sm" },
              el("span", { className: "text-emerald-600" }, "\u2713"),
              el("span", null, "Items not available in department stores")
            ),
            r.specs_are_broad && el("div", { className: "flex items-center gap-2 text-sm" },
              el("span", { className: "text-emerald-600" }, "\u2713"),
              el("span", null, "Specifications are broad and non-restrictive")
            )
          )
        )
      )
    ),
    // Edit / amendment Audit Trail
    el(AuditTrail, { entries: r.audit_trail || [] })
  );
}

export default RequisitionView;
